import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { Auth } from '#api';
import { handleServerFormError, ValidationError, type ErrorDetails, type RewordedErrorDetails } from '#api/errors.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { Divider } from '#components/atoms/divider/Divider.tsx';
import { ErrorMessage, Input } from '#components/atoms/forms/index.ts';
import { Link } from '#components/atoms/link/Link.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { AuthLayout } from '#components/patterns/layouts/auth/AuthLayout.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import { isWebAuthnSupported } from '#util/webauthn-support.ts';
import { useWebAuthnLogin } from './hooks/useWebAuthnLogin.ts';

type FormData = {
  email: string;
};

type Props = {
  email?: string;
  onSuccessfulSubmit: (email: string) => void;
};

/**
 * One email-first entry point. The login method is chosen by the email's domain, never by whether
 * an account exists: every address at an SSO domain gets the SSO choice, and every other address
 * gets a magic link (which the API silently drops for unknown addresses).
 */
export function Login({ onSuccessfulSubmit, email }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
  } = useForm<FormData>();
  const auth = useAuth();
  const navigate = useNavigate();
  const magicLinkMutation = useMutation({ mutationFn: auth.sendMagicLink });
  // Only asks whether the domain has SSO; the token it returns is discarded so that the redirect
  // uses a fresh one from ssoMutation, however long the user lingers on the choice.
  const discoverSSOMutation = useMutation({ mutationFn: Auth.startOIDCAuthByEmail });
  const ssoMutation = useMutation({ mutationFn: auth.startOIDCByEmail });
  const [ssoEmail, setSsoEmail] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const webAuthnSupported = isWebAuthnSupported();
  const {
    webAuthnMutation,
    webAuthnError,
    clearError: clearWebAuthnError,
  } = useWebAuthnLogin({
    onSuccess: () => void navigate('/', { replace: true }),
  });

  function mapServerErrorsToForm(error: ErrorDetails | RewordedErrorDetails) {
    const { path, message } = error;
    switch (path) {
      case 'email':
        setError(path, { message }, { shouldFocus: true });
        return true;
      case undefined:
      default:
        setFormError(message);
        return true;
    }
  }

  async function sendMagicLink(address: string) {
    try {
      await magicLinkMutation.mutateAsync(address);
      onSuccessfulSubmit(address);
    } catch (error) {
      if (error instanceof ValidationError) {
        // "Invalid credentials" errors have no path, but we want them under the email field
        const errs = error.errors.map((err) => ({ ...err, path: err.path ?? 'email' }));
        handleServerFormError(new ValidationError(errs, error.response), mapServerErrorsToForm);
      } else {
        handleServerFormError(error, mapServerErrorsToForm);
      }
    }
  }

  const submitEmail = handleSubmit(async ({ email: address }) => {
    try {
      await discoverSSOMutation.mutateAsync({ email: address });
      setSsoEmail(address);
    } catch (error) {
      const emailError =
        error instanceof ValidationError ?
          error.errors.find((err) => err.path === 'email' || err.code === 'ERR_INVALID_EMAIL')
        : undefined;
      if (emailError) {
        setError('email', { message: emailError.message }, { shouldFocus: true });
        return;
      }
      // Discovery runs the full SSO start, so a provider that's down or misconfigured fails it too.
      // A magic link still works then, and is what every non-SSO domain gets anyway.
      await sendMagicLink(address);
    }
  });

  async function continueWithSSO(address: string) {
    setFormError(null);
    try {
      await ssoMutation.mutateAsync(address);
    } catch (error) {
      handleServerFormError(error, mapServerErrorsToForm);
    }
  }

  function startOver() {
    setFormError(null);
    setSsoEmail(null);
  }

  useEffect(() => {
    if (email) {
      setValue('email', email);
    }
  }, [email, setValue]);

  // The domain has admin SSO. Shown for every address at the domain, account or not.
  if (ssoEmail) {
    const pending = ssoMutation.isPending || magicLinkMutation.isPending;
    return (
      <AuthLayout>
        <div className="simple-page-content">
          <h1 className="h2 mb-8">Log in to your account</h1>
          <p className="p-bold mb-32">
            <span className="wrap-anywhere">{ssoEmail}</span> can log in with your organization’s Single Sign-On.
          </p>
          <Spacer size="xwide" direction="vertical">
            {formError ?
              <ErrorMessage>{formError}</ErrorMessage>
            : null}
            <Button className="width-100" disabled={pending} onClick={() => void continueWithSSO(ssoEmail)}>
              Continue with SSO
            </Button>
            <Button
              className="width-100"
              variant="secondary"
              disabled={pending}
              onClick={() => {
                setFormError(null);
                void sendMagicLink(ssoEmail);
              }}
            >
              Email me a login link instead
            </Button>
            <Button className="width-100" variant="link" onClick={startOver}>
              Use a different email
            </Button>
          </Spacer>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      headerText={
        <div>
          Don’t have an account?{' '}
          <Link className="a" to="/signup">
            Sign up
          </Link>
        </div>
      }
    >
      <form className="simple-page-content" method="post" onSubmit={(e) => void submitEmail(e)}>
        <h1 className="h2 mb-32">Log in to your account</h1>
        <Field id="email" label="What is your email?" className="mb-32">
          <Input
            className="width-100"
            type="email"
            defaultValue={email}
            hasError={Boolean(errors.email)}
            enablePWManagers={true}
            autoComplete="username webauthn"
            {...register('email', {
              required: 'What’s your email address?',
              minLength: { value: 3, message: 'Email address must be at least 3 characters' },
              maxLength: {
                value: 800,
                message: 'Whoa that’s a long email.  Sorry, but it needs to be 800 characters or fewer.',
              },
            })}
          />
          {errors.email && <ErrorMessage className="mt-8">{errors.email.message}</ErrorMessage>}
        </Field>
        <Spacer size="xwide" direction="vertical">
          <Button
            className="width-100"
            disabled={discoverSSOMutation.isPending || magicLinkMutation.isPending}
            type="submit"
          >
            Log in
          </Button>
          {webAuthnSupported || auth.oidcLoginURL ?
            <Divider>OR</Divider>
          : null}
          {webAuthnSupported ?
            <>
              <Button
                className="width-100"
                variant="secondary"
                disabled={webAuthnMutation.isPending}
                onClick={() => {
                  clearWebAuthnError();
                  webAuthnMutation.mutate();
                }}
              >
                {webAuthnMutation.isPending ? 'Waiting for device...' : 'Log in with passkey'}
              </Button>
              {webAuthnError ?
                <ErrorMessage>{webAuthnError}</ErrorMessage>
              : null}
            </>
          : null}
          {/* Set by a previous SSO login, so this returns to the same organization's provider */}
          {auth.oidcLoginURL ?
            <LinkButton className="width-100" variant="secondary" to={auth.oidcLoginURL}>
              Log in with SSO
            </LinkButton>
          : null}
        </Spacer>
      </form>
    </AuthLayout>
  );
}
